import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  useQueryStates,
} from 'nuqs';
import type {
  RecertificationTimingSortBy,
  RecertificationTimingSortDirection,
} from '../../services/api/types.gen';
import { useDebounce } from '../useDebounce';
import {
  createReportSortParser,
  parseAsReportSortDirection,
} from './reportQueryParsers';

const SORT_KEYS = [
  'patient',
  'clinic_name',
  'episode_start_date',
  'episode_end_date',
  'assessment_date',
  'episode_day',
  'timing_status',
] as const;
const parsers = {
  clinic: parseAsString.withDefault(''),
  q: parseAsString.withDefault(''),
  only_failed: parseAsBoolean.withDefault(false),
  sort: createReportSortParser(SORT_KEYS).withDefault('assessment_date'),
  dir: parseAsReportSortDirection.withDefault('asc'),
  page: parseAsInteger.withDefault(1),
  size: parseAsInteger.withDefault(25),
};
type RecertificationTimingUrlState = {
  clinic: string;
  q: string;
  only_failed: boolean;
  sort: (typeof SORT_KEYS)[number];
  dir: 'asc' | 'desc';
  page: number;
  size: number;
};
type Filters = Partial<RecertificationTimingUrlState>;

export interface RecertificationTimingReportParams {
  clinicId: string;
  query: string;
  onlyFailed: boolean;
  sortBy: (typeof SORT_KEYS)[number];
  sortDirection: 'asc' | 'desc';
  page: number;
  pageSize: number;
}
type FilterUpdates = {
  clinicId?: string;
  query?: string;
  onlyFailed?: boolean;
};

export function useRecertificationTimingReportParams() {
  const [url, setUrl] = useQueryStates(parsers, {
    history: 'replace',
    clearOnDefault: true,
  });
  const [searchInputState, setSearchInputState] = useState(url.q);
  const update = useCallback(
    (updates: Filters, reset = true) =>
      setUrl({ ...updates, ...(reset ? { page: 1 } : {}) }),
    [setUrl]
  );
  const setSearchQuery = useCallback(
    (value: string) => update({ q: value.trim() }),
    [update]
  );
  const debouncedSearch = useDebounce(setSearchQuery, 300);
  const setSearchInput = useCallback(
    (value: string) => {
      setSearchInputState(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );
  useEffect(() => {
    debouncedSearch.cancel();
    setSearchInputState(url.q);
  }, [debouncedSearch, url.q]);
  const params = useMemo<RecertificationTimingReportParams>(
    () => ({
      clinicId: url.clinic,
      query: url.q,
      onlyFailed: url.only_failed,
      sortBy: url.sort,
      sortDirection: url.dir,
      page: url.page,
      pageSize: url.size,
    }),
    [url]
  );
  const tableQuery = useMemo(
    () => ({
      clinic_id: url.clinic || undefined,
      q: url.q || undefined,
      only_failed: url.only_failed,
      sort_by: url.sort,
      sort_direction: url.dir,
      page: url.page,
      page_size: url.size,
    }),
    [url]
  );
  return {
    params,
    searchInput: searchInputState,
    setSearchInput,
    setSearchQuery: debouncedSearch,
    isSearchPending: searchInputState !== url.q,
    setFilters: (filters: FilterUpdates) => {
      const updates: Filters = {};
      if (filters.clinicId !== undefined) updates.clinic = filters.clinicId;
      if (filters.query !== undefined) updates.q = filters.query;
      if (filters.onlyFailed !== undefined) {
        updates.only_failed = filters.onlyFailed;
      }
      return update(updates);
    },
    setSorting: (
      sort: RecertificationTimingSortBy,
      dir: RecertificationTimingSortDirection
    ) => update({ sort, dir }),
    setPagination: (page: number, size: number) =>
      update({ page, size }, false),
    tableQuery,
    exportQuery: tableQuery,
  };
}
